/** O mínimo que `representativeRate` lê de um quarto: só CONTA quantas
 *  políticas existem (`cancellationPolicies?.length`) e compara `total.amount`
 *  — nunca lê o QUE HÁ dentro de cada política. É por isso que o tipo é
 *  estrutural (`readonly unknown[]`) em vez de `Pick<ChatUiHotelRoom, ...>`:
 *  o drawer de detalhes chama com o `cancellationPolicies` tipado do genui
 *  (`{from, until, penalty}[]`), e o agents chama com o `unknown[]` solto do
 *  fornecedor (ver `toRepresentativeCandidate` em `core/state.ts`) — as duas
 *  formas só precisam concordar na CONTAGEM, não no conteúdo de cada item. */
interface RepresentativeRateCandidate {
  cancellationPolicies?: readonly unknown[];
  negotiated?: boolean;
  total?: { amount?: number };
}

/** A tarifa que REPRESENTA o hotel no cancelamento: a negociada, senão a mais
 *  barata entre as que declaram política. Compartilhada pelo drawer de
 *  detalhes e pelo foco do agente para os dois falarem da MESMA tarifa.
 *
 *  Genérica (não fixada em `ChatUiHotelRoom`): o agents chama com um quarto
 *  ADAPTADO (`totalAmount`/`currency` soltos do fornecedor viram `total`,
 *  ver `focusCancellation` em `core/state.ts`) que carrega campos extras
 *  (`refundable`) — o genérico devolve o MESMO tipo de entrada, preservando
 *  esses campos extras no retorno. */
export function representativeRate<T extends RepresentativeRateCandidate>(
  rooms: readonly T[],
): T | undefined {
  const withPolicy = rooms.filter((room) => room.cancellationPolicies?.length);
  if (withPolicy.length === 0) return undefined;
  return (
    withPolicy.find((room) => room.negotiated) ??
    withPolicy.reduce<T | undefined>(
      (cheapest, room) =>
        cheapest === undefined ||
        (room.total?.amount ?? Number.POSITIVE_INFINITY) <
          (cheapest.total?.amount ?? Number.POSITIVE_INFINITY)
          ? room
          : cheapest,
      undefined,
    )
  );
}
